import { useCallback, useEffect, useRef } from 'react';

/**
 * rxjs `debounceTime(delayMs)` followed by `distinctUntilChanged()` on a Subject of strings: the returned function
 * plays the role of `subject.next(value)`; `onValue` runs only when the debounced value differs from the last one
 * that was emitted (the first value always emits). The pending timer is dropped on unmount.
 */
export function useDebouncedDistinct(onValue: (value: string) => void, delayMs = 300): (value: string) => void {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const last = useRef<string | undefined>(undefined);
  const handler = useRef(onValue);

  useEffect(() => {
    handler.current = onValue;
  });

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(
    (value: string) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (last.current !== value) {
          last.current = value;
          handler.current(value);
        }
      }, delayMs);
    },
    [delayMs],
  );
}
